import {
  FlowType,
  Step as ModelStep,
  ReturnFlow,
  StepFn,
  stepsBuildWithMapping,
} from "@redotech/redo-model/return-flow";
import type { SetReturnLocationsState } from "@redotech/redo-model/return-flow-step-valid";
import { Condition } from "@redotech/redo-model/return-flow/condition";
import * as React from "react";
import {
  ASSIGN_TO_AGENT,
  AssignToAgentState,
} from "./chat-flow/assign-to-agent";
import {
  CHAT_MULTIPLE_CHOICE,
  ChatMultipleChoiceState,
} from "./chat-flow/chat-multiple-choice";
import { CLOSE_CHAT, CloseChatState } from "./chat-flow/close-chat";
import {
  CUSTOMER_RESPONSE,
  CustomerResponseState,
} from "./chat-flow/customer-response";
import { REENTRY, ReentryState } from "./chat-flow/reentry";
import { SEE_ORDERS, SeeOrdersState } from "./chat-flow/see-orders";
import { SEND_MESSAGE, SendMessageState } from "./chat-flow/send-message";
import { AB_TEST, AbTestState } from "./return-flow/ab-test";
import {
  ADD_RETURN_TAG,
  AddReturnTagState,
  getTagTypeName,
} from "./return-flow/add-return-tag";
import {
  ADD_UNDECLARED_ITEM,
  AddUndeclaredItemState,
} from "./return-flow/add-undeclared-item";
import {
  AI_RETURN_REASONS,
  AiReturnReasonsState,
} from "./return-flow/ai-return-reasons";
import { BLOCK, BlockState } from "./return-flow/block";
import { COMBINE_LABEL, CombineLabelState } from "./return-flow/combine-label";
import {
  CONDITION,
  ConditionState,
  ConditionTypeDetailsProps,
} from "./return-flow/condition";
import {
  DISCOUNT_CODE_NAME,
  DiscountCodeNameState,
} from "./return-flow/discount-code-name";
import { DO_NOTHING, DoNothingState } from "./return-flow/do-nothing";
import {
  DYNAMIC_BUNDLE_ADJUSTMENT,
  DynamicBundleAdjustmentState,
} from "./return-flow/dynamic-bundle-adjustment";
import { EVIDENCE, EvidenceState } from "./return-flow/evidence";
import {
  EXCHANGE_SHIPPING_NAME,
  ExchangeShippingNameState,
} from "./return-flow/exchange-shipping-name";
import {
  FINAL_SALE_RETURN,
  FinalSaleReturnState,
} from "./return-flow/final-sale";
import { FLAG, FlagState } from "./return-flow/flag";
import { INFORMATION, InformationState } from "./return-flow/information";
import { INPUT, InputState } from "./return-flow/input";
import { LICENSE_PLATE, LicensePlateState } from "./return-flow/license-plate";
import { MANUAL_REVIEW, ManualReviewState } from "./return-flow/manual-review";
import {
  getReturnReasonStep,
  MULTIPLE_CHOICE,
  MultipleChoiceState,
} from "./return-flow/multiple-choice";
import {
  OVERRIDE_ADJUSTMENT,
  OverrideAdjustmentState,
} from "./return-flow/override-adjustment";
import {
  OVERRIDE_GREEN_RETURN,
  OverrideGreenReturnState,
} from "./return-flow/override-green-return";
import { REJECT, RejectState } from "./return-flow/reject";
import { RETURN, ReturnState } from "./return-flow/return";
import {
  RETURN_PROCESSING_DECISION,
  ReturnProcessingDecisionState,
} from "./return-flow/return-processing-decision";
import {
  RETURN_PROCESSING_INFORMATION,
  ReturnProcessingInformationState,
} from "./return-flow/return-processing-information";
import { SEND_EMAIL, SendEmailState } from "./return-flow/send-email";
import { SEND_SMS, SendSmsState } from "./return-flow/send-sms";
import {
  SEPARATE_LABELS,
  SeparateLabelsState,
} from "./return-flow/separate-labels";
import { SET_RETURN_LOCATIONS } from "./return-flow/set-return-locations";
import { SORT, SortState } from "./return-flow/sort";
import { SUBMIT_CLAIM, SubmitClaimState } from "./return-flow/submit-claim";
import { SUBMIT_REPAIR, SubmitRepairState } from "./return-flow/submit-repair";
import {
  SUBMIT_WARRANTY,
  SubmitWarrantyState,
} from "./return-flow/submit-warranty";
import {
  SUBMIT_WARRANTY_REGISTRATION,
  SubmitWarrantyRegistrationState,
} from "./return-flow/submit-warranty-registration";
import { TRIGGER, TriggerState } from "./return-flow/trigger";
import { WAIT, WaitState } from "./return-flow/wait";
import {
  DetailsComponentsForActions,
  MULTIPLE_ACTIONS,
  MultipleActionsState,
} from "./rules/multiple-actions";
import {
  DetailsComponentsForRuleConditions,
  MULTIPLE_CONDITIONS,
  MultipleConditionsState,
} from "./rules/multiple-conditions";
import { getStartStep, StartState } from "./start";
import {
  Step,
  StepId,
  StepType,
  StepTypeDetailsProps,
  TypedStep,
} from "./step";

function invalidStepType(s: never): never {
  throw new Error("Invalid step type");
}

export interface DetailsComponentsForSteps {
  Start: React.ComponentType<StepTypeDetailsProps<StartState>>;
  MultipleChoice: React.ComponentType<
    StepTypeDetailsProps<MultipleChoiceState>
  >;
  Flag: React.ComponentType<StepTypeDetailsProps<FlagState>>;
  ManualReview: React.ComponentType<StepTypeDetailsProps<ManualReviewState>>;
  DoNothing: React.ComponentType<StepTypeDetailsProps<DoNothingState>>;
  SubmitWarrantyRegistration: React.ComponentType<
    StepTypeDetailsProps<SubmitWarrantyRegistrationState>
  >;
  Input: React.ComponentType<StepTypeDetailsProps<InputState>>;
  Information: React.ComponentType<StepTypeDetailsProps<InformationState>>;
  ExchangeShippingName: React.ComponentType<
    StepTypeDetailsProps<ExchangeShippingNameState>
  >;
  DiscountCodeName: React.ComponentType<
    StepTypeDetailsProps<DiscountCodeNameState>
  >;
  Reject: React.ComponentType<StepTypeDetailsProps<RejectState>>;
  OverrideAdjustment: React.ComponentType<
    StepTypeDetailsProps<OverrideAdjustmentState>
  >;
  OverrideGreenReturn: React.ComponentType<
    StepTypeDetailsProps<OverrideGreenReturnState>
  >;
  Return: React.ComponentType<StepTypeDetailsProps<ReturnState>>;
  FinalSaleReturn: React.ComponentType<
    StepTypeDetailsProps<FinalSaleReturnState>
  >;
  SubmitClaim: React.ComponentType<StepTypeDetailsProps<SubmitClaimState>>;
  SubmitWarranty: React.ComponentType<
    StepTypeDetailsProps<SubmitWarrantyState>
  >;
  SubmitRepair: React.ComponentType<StepTypeDetailsProps<SubmitRepairState>>;
  Block: React.ComponentType<StepTypeDetailsProps<BlockState>>;
  ChatMultipleChoice: React.ComponentType<
    StepTypeDetailsProps<ChatMultipleChoiceState>
  >;
  CloseChat: React.ComponentType<StepTypeDetailsProps<CloseChatState>>;
  CustomerResponse: React.ComponentType<
    StepTypeDetailsProps<CustomerResponseState>
  >;
  AssignToAgent: React.ComponentType<StepTypeDetailsProps<AssignToAgentState>>;
  SendMessage: React.ComponentType<StepTypeDetailsProps<SendMessageState>>;
  Trigger: React.ComponentType<StepTypeDetailsProps<TriggerState>>;
  Wait: React.ComponentType<StepTypeDetailsProps<WaitState>>;
  SendEmail: React.ComponentType<StepTypeDetailsProps<SendEmailState>>;
  SendSms: React.ComponentType<StepTypeDetailsProps<SendSmsState>>;
  SeeOrders: React.ComponentType<StepTypeDetailsProps<SeeOrdersState>>;
  Reentry: React.ComponentType<StepTypeDetailsProps<ReentryState>>;
  AbTest: React.ComponentType<StepTypeDetailsProps<AbTestState>>;
  AddReturnTag: React.ComponentType<StepTypeDetailsProps<AddReturnTagState>>;
  DynamicBundleAdjustment: React.ComponentType<
    StepTypeDetailsProps<DynamicBundleAdjustmentState>
  >;
  AiReturnReasons: React.ComponentType<
    StepTypeDetailsProps<AiReturnReasonsState>
  >;
  Condition: React.ComponentType<StepTypeDetailsProps<ConditionState>>;
  MultipleConditions: React.ComponentType<
    StepTypeDetailsProps<MultipleConditionsState>
  >;
  MultipleActions: React.ComponentType<
    StepTypeDetailsProps<MultipleActionsState>
  >;
  SetReturnLocations: React.ComponentType<
    StepTypeDetailsProps<SetReturnLocationsState>
  >;
  CombineLabel: React.ComponentType<StepTypeDetailsProps<CombineLabelState>>;
  SeparateLabels: React.ComponentType<
    StepTypeDetailsProps<SeparateLabelsState>
  >;
  ReturnProcessingDecision: React.ComponentType<
    StepTypeDetailsProps<ReturnProcessingDecisionState>
  >;
  ReturnProcessingInformation: React.ComponentType<
    StepTypeDetailsProps<ReturnProcessingInformationState>
  >;
  LicensePlate: React.ComponentType<StepTypeDetailsProps<LicensePlateState>>;
  Sort: React.ComponentType<StepTypeDetailsProps<SortState>>;
  Evidence: React.ComponentType<StepTypeDetailsProps<EvidenceState>>;
  AddUndeclaredItem: React.ComponentType<
    StepTypeDetailsProps<AddUndeclaredItemState>
  >;
}

export const NullDetails = () => null;
const defaultGetConditionDetails = (condition: Condition) => NullDetails;
const NullRuleConditionDetails: DetailsComponentsForRuleConditions = {
  AfterHoursDetails: NullDetails,
  TicketChannelDetails: NullDetails,
  SubjectLineDetails: NullDetails,
  MessageBodyMatchesDetails: NullDetails,
  TopicDetails: NullDetails,
  ReceivedAtEmailDetails: NullDetails,
  SentFromEmailDetails: NullDetails,
  AssignmentTypeDetails: NullDetails,
};
const NullDetailsComponentsForActions: DetailsComponentsForActions = {
  SetStatusDetails: NullDetails,
  AddTagDetails: NullDetails,
  AutoReplyDetails: NullDetails,
  AssignDetails: NullDetails,
};

export const NullDetailsForSteps: DetailsComponentsForSteps = {
  Start: NullDetails,
  MultipleChoice: NullDetails,
  Flag: NullDetails,
  ManualReview: NullDetails,
  DoNothing: NullDetails,
  SubmitWarrantyRegistration: NullDetails,
  Input: NullDetails,
  Information: NullDetails,
  ExchangeShippingName: NullDetails,
  DiscountCodeName: NullDetails,
  Reject: NullDetails,
  OverrideAdjustment: NullDetails,
  OverrideGreenReturn: NullDetails,
  Return: NullDetails,
  FinalSaleReturn: NullDetails,
  SubmitClaim: NullDetails,
  SubmitWarranty: NullDetails,
  SubmitRepair: NullDetails,
  Block: NullDetails,
  ChatMultipleChoice: NullDetails,
  CloseChat: NullDetails,
  CustomerResponse: NullDetails,
  AssignToAgent: NullDetails,
  SendMessage: NullDetails,
  Trigger: NullDetails,
  Wait: NullDetails,
  SendEmail: NullDetails,
  SendSms: NullDetails,
  SeeOrders: NullDetails,
  Reentry: NullDetails,
  AbTest: NullDetails,
  AddReturnTag: NullDetails,
  DynamicBundleAdjustment: NullDetails,
  AiReturnReasons: NullDetails,
  Condition: NullDetails,
  MultipleConditions: NullDetails,
  MultipleActions: NullDetails,
  SetReturnLocations: NullDetails,
  CombineLabel: NullDetails,
  SeparateLabels: NullDetails,
  ReturnProcessingDecision: NullDetails,
  ReturnProcessingInformation: NullDetails,
  LicensePlate: NullDetails,
  Sort: NullDetails,
  Evidence: NullDetails,
  AddUndeclaredItem: NullDetails,
};

export const nullModelToStepsDetailsProps = {
  getDetailsForCondition: defaultGetConditionDetails,
  DetailsForRuleConditions: NullRuleConditionDetails,
  DetailsForActions: NullDetailsComponentsForActions,
  DetailsForSteps: NullDetailsForSteps,
};

export function modelToSteps({
  model,
  DetailsForSteps,
  getDetailsForCondition,
  DetailsForRuleConditions,
  DetailsForActions,
  firstStepTitle,
  returnReasonTitle,
  flowType,
  startIcon,
}: {
  model: ReturnFlow;
  DetailsForSteps: DetailsComponentsForSteps;
  getDetailsForCondition: (
    condition: Condition,
  ) => React.ComponentType<ConditionTypeDetailsProps<any>>;
  DetailsForRuleConditions: DetailsComponentsForRuleConditions;
  DetailsForActions: DetailsComponentsForActions;
  firstStepTitle?: string;
  returnReasonTitle?: string;
  flowType?: FlowType;
  startIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}): Map<StepId, Step> {
  // start must come first, to ensure that stepsToModel places that item first as well
  const startState: { next: StepId | undefined } = { next: undefined };
  const result = new Map<StepId, Step>(
    firstStepTitle
      ? [
          [
            "start",
            new TypedStep(
              getStartStep(firstStepTitle, DetailsForSteps.Start, startIcon),
              startState,
            ),
          ],
        ]
      : [],
  );
  for (const [index, step] of model.steps.entries()) {
    let type: StepType<any, any>;
    switch (step.type) {
      case ModelStep.CONDITION:
        type = CONDITION(DetailsForSteps.Condition, getDetailsForCondition);
        break;
      case ModelStep.INPUT:
        type = INPUT(DetailsForSteps.Input);
        break;
      case ModelStep.INFORMATION:
        type = INFORMATION(DetailsForSteps.Information);
        break;
      case ModelStep.MANUAL_REVIEW:
        type = MANUAL_REVIEW(DetailsForSteps.ManualReview);
        break;
      case ModelStep.FLAG:
        type = FLAG(DetailsForSteps.Flag);
        break;
      case ModelStep.EXCHANGE_SHIPPING_NAME:
        type = EXCHANGE_SHIPPING_NAME(DetailsForSteps.ExchangeShippingName);
        break;
      case ModelStep.DISCOUNT_CODE_NAME:
        type = DISCOUNT_CODE_NAME(DetailsForSteps.DiscountCodeName);
        break;
      case ModelStep.MULTIPLE_CHOICE:
        type = step.reason
          ? getReturnReasonStep(
              returnReasonTitle!,
              DetailsForSteps.MultipleChoice,
            )
          : MULTIPLE_CHOICE(DetailsForSteps.MultipleChoice);
        break;
      case ModelStep.REJECT:
        type = REJECT(DetailsForSteps.Reject);
        break;
      case ModelStep.OVERRIDE_ADJUSTMENT:
        type = OVERRIDE_ADJUSTMENT(DetailsForSteps.OverrideAdjustment);
        break;
      case ModelStep.OVERRIDE_GREEN_RETURN:
        type = OVERRIDE_GREEN_RETURN(DetailsForSteps.OverrideGreenReturn);
        break;
      case ModelStep.RETURN:
        type = RETURN(DetailsForSteps.Return);
        break;
      case ModelStep.FINAL_SALE_RETURN:
        type = FINAL_SALE_RETURN(DetailsForSteps.FinalSaleReturn);
        break;
      case ModelStep.SUBMIT_CLAIM:
        type = SUBMIT_CLAIM(DetailsForSteps.SubmitClaim);
        break;
      case ModelStep.SUBMIT_WARRANTY:
        type = SUBMIT_WARRANTY(DetailsForSteps.SubmitWarranty);
        break;
      case ModelStep.SUBMIT_REPAIR:
        type = SUBMIT_REPAIR(DetailsForSteps.SubmitRepair);
        break;
      case ModelStep.BLOCK:
        type = BLOCK(DetailsForSteps.Block);
        break;
      case ModelStep.CHAT_MULTIPLE_CHOICE:
        type = CHAT_MULTIPLE_CHOICE(DetailsForSteps.ChatMultipleChoice);
        break;
      case ModelStep.CLOSE_CHAT:
        type = CLOSE_CHAT(DetailsForSteps.CloseChat);
        break;
      case ModelStep.CUSTOMER_RESPONSE:
        type = CUSTOMER_RESPONSE(DetailsForSteps.CustomerResponse);
        break;
      case ModelStep.ASSIGN_TO_AGENT:
        type = ASSIGN_TO_AGENT(DetailsForSteps.AssignToAgent);
        break;
      case ModelStep.SEND_MESSAGE:
        type = SEND_MESSAGE(DetailsForSteps.SendMessage);
        break;
      case ModelStep.REENTRY:
        type = REENTRY(DetailsForSteps.Reentry);
        break;
      case ModelStep.TRIGGER:
        type = TRIGGER(DetailsForSteps.Trigger);
        break;
      case ModelStep.WAIT:
        type = WAIT(DetailsForSteps.Wait);
        break;
      case ModelStep.SEND_EMAIL:
        type = SEND_EMAIL(DetailsForSteps.SendEmail);
        break;
      case ModelStep.SEND_SMS:
        type = SEND_SMS(DetailsForSteps.SendSms);
        break;
      case ModelStep.SEE_ORDERS:
        type = SEE_ORDERS(DetailsForSteps.SeeOrders);
        break;
      case ModelStep.MULTIPLE_CONDITIONS:
        type = MULTIPLE_CONDITIONS(
          DetailsForSteps.MultipleConditions,
          DetailsForRuleConditions,
        );
        break;
      case ModelStep.MULTIPLE_ACTIONS:
      // fallthrough
      case ModelStep.ACTION:
        type = MULTIPLE_ACTIONS(
          DetailsForSteps.MultipleActions,
          DetailsForActions,
        );
        break;
      case ModelStep.AB_TEST:
        type = AB_TEST(DetailsForSteps.AbTest);
        break;
      case ModelStep.DO_NOTHING:
        type = DO_NOTHING(DetailsForSteps.DoNothing);
        break;
      case ModelStep.SUBMIT_WARRANTY_REGISTRATION:
        type = SUBMIT_WARRANTY_REGISTRATION(
          DetailsForSteps.SubmitWarrantyRegistration,
        );
        break;
      case ModelStep.EW_UPSELL:
        throw new Error("EW_UPSELL is not supported in the flow editor");
      case ModelStep.AI_RETURN_REASONS:
        type = AI_RETURN_REASONS(DetailsForSteps.AiReturnReasons);
        break;
      case ModelStep.ADD_RETURN_TAG: {
        const tagTypeName = getTagTypeName(flowType);
        const addReturnTagTitle = `Add ${tagTypeName.toLowerCase()} tag`;
        type = ADD_RETURN_TAG(DetailsForSteps.AddReturnTag, addReturnTagTitle);
        break;
      }
      case ModelStep.DYNAMIC_BUNDLE_ADJUSTMENT:
        type = DYNAMIC_BUNDLE_ADJUSTMENT(
          DetailsForSteps.DynamicBundleAdjustment,
        );
        break;
      case ModelStep.SET_RETURN_LOCATIONS:
        type = SET_RETURN_LOCATIONS(DetailsForSteps.SetReturnLocations);
        break;
      case ModelStep.COMBINE_LABEL:
        type = COMBINE_LABEL(DetailsForSteps.CombineLabel);
        break;
      case ModelStep.SEPARATE_LABELS:
        type = SEPARATE_LABELS(DetailsForSteps.SeparateLabels);
        break;
      case ModelStep.RETURN_PROCESSING_DECISION:
        type = RETURN_PROCESSING_DECISION(
          DetailsForSteps.ReturnProcessingDecision,
        );
        break;
      case ModelStep.RETURN_PROCESSING_INFORMATION:
        type = RETURN_PROCESSING_INFORMATION(
          DetailsForSteps.ReturnProcessingInformation,
        );
        break;
      case ModelStep.LICENSE_PLATE:
        type = LICENSE_PLATE(DetailsForSteps.LicensePlate);
        break;
      case ModelStep.SORT:
        type = SORT(DetailsForSteps.Sort);
        break;
      case ModelStep.EVIDENCE:
        type = EVIDENCE(DetailsForSteps.Evidence);
        break;
      case ModelStep.ADD_UNDECLARED_ITEM:
        type = ADD_UNDECLARED_ITEM(DetailsForSteps.AddUndeclaredItem);
        break;
      case ModelStep.VERIFICATION:
        throw new Error("VERIFICATION is not supported in the flow editor");
      default:
        invalidStepType(step);
    }
    const id = String(index);
    if (startState.next === undefined) {
      startState.next = id;
    }
    result.set(id, new TypedStep(type, type.fromModel(step, String)));
  }
  return result;
}

export function stepsToModel(
  steps: Map<StepId, Step>,
  options?: { defaultResetStepId?: StepId },
): ReturnFlow {
  const stepFns = new Map<StepId, StepFn>();
  for (const [id, step] of steps) {
    if (step.start && step.ignore()) {
      stepFns.set(step.start, undefined!); // ensure first step is first
      continue;
    }
    stepFns.set(id, (idFn) => step.toModel((id) => idFn(stepFns.get(id)!)));
  }

  const { steps: newSteps, idByStepFn } = stepsBuildWithMapping(
    stepFns.values(),
  );

  let defaultResetStep: number | undefined;
  if (options?.defaultResetStepId != null) {
    const fn = stepFns.get(options.defaultResetStepId);
    if (fn) {
      defaultResetStep = idByStepFn.get(fn);
    }
  }

  return { steps: newSteps, defaultResetStep, type: FlowType.RETURN };
}
